// Thin wrapper over HeroUI's toast queue so the rest of the app never imports
// the HeroUI toast API directly (single place for colours/timeouts, and easy
// to mock in tests). `<ToastProvider />` is mounted once in main.tsx.
import { addToast, closeToast } from '@heroui/react'
import type { ReactNode } from 'react'

export type ToastInput = {
	title: ReactNode
	description?: ReactNode
	endContent?: ReactNode
	/** ms until auto-close; 0 keeps the toast until closed programmatically */
	timeout?: number
	hideCloseButton?: boolean
	onClose?: () => void
}

const ERROR_TIMEOUT_MS = 10_000
const SUCCESS_TIMEOUT_MS = 5_000

const lastShownAt = new Map<string, number>()

export const toast = {
	error(input: ToastInput): string | null {
		return addToast({ color: 'danger', timeout: ERROR_TIMEOUT_MS, ...input })
	},

	/**
	 * Like `error`, but suppresses repeats of the same `key` within `windowMs`.
	 * Used by periodic callers (autosave) so a persistent failure does not stack
	 * a new toast every 30 seconds.
	 */
	errorOnce(key: string, input: ToastInput, windowMs = 60_000): string | null {
		const now = Date.now()
		const last = lastShownAt.get(key)
		if (last !== undefined && now - last < windowMs) return null
		lastShownAt.set(key, now)
		return toast.error(input)
	},

	warning(input: ToastInput): string | null {
		return addToast({ color: 'warning', timeout: ERROR_TIMEOUT_MS, ...input })
	},

	success(input: ToastInput): string | null {
		return addToast({ color: 'success', timeout: SUCCESS_TIMEOUT_MS, ...input })
	},

	/** Persistent toast (no auto-close). Caller must `close(key)` it. */
	sticky(color: 'danger' | 'warning' | 'default', input: ToastInput): string | null {
		return addToast({ color, timeout: 0, shouldShowTimeoutProgress: false, ...input })
	},

	close(key: string): void {
		closeToast(key)
	},

	/** Exposed for tests only. */
	__resetForTests(): void {
		lastShownAt.clear()
	},
}
